import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faFileText, 
  faSpinner, 
  faSearch,
  faFilter,
  faExclamationTriangle,
  faCircleCheck,
  faCircleXmark,
  faPowerOff,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';

const CatiSurveys = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Roles that see both CATI and F2F surveys side-by-side on the list page
  const isChargeEtude = user && (user.role === 'charge_etude' || user.role === 'charge_etude_superieur' || user.role === 'resp_controle' || user.role === 'chef_terrain_superieur');
  const isChefTerrain = user && user.role === 'chef_terrain';
  // supervisor_cati has read-only access — no toggle-status buttons
  const readOnly = user?.role === 'supervisor_cati';
  
  // Tab state for charge_etude (0 = CATI, 1 = Face à Face)
  const [activeTab, setActiveTab] = useState(0);
  
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [togglingSurveyId, setTogglingSurveyId] = useState(null);
  
  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    fetchSurveys();
  }, []);

  useEffect(() => {
    filterSurveys();
  }, [searchTerm, statusFilter, paymentTypeFilter, surveys]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cati/surveys');
      if (response.data.success) {
        setSurveys(response.data.data || []);
      } else {
        setSurveys([]);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setError('Erreur lors du chargement des enquêtes');
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const filterSurveys = () => {
    let filtered = [...surveys];

    // Chef terrain only sees F2F surveys
    if (isChefTerrain) {
      filtered = filtered.filter(survey => survey.poste_qcode === 'ref');
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(survey =>
        survey.title?.toLowerCase().includes(searchLower) ||
        survey.limesurvey_id?.toLowerCase().includes(searchLower) ||
        survey.poste_qcode?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter(survey => survey.is_active === true);
      } else {
        filtered = filtered.filter(survey => survey.is_active === false);
      }
    }

    // Payment type filter
    if (paymentTypeFilter !== 'all') {
      filtered = filtered.filter(survey => (survey.payment_type || 'per_complete') === paymentTypeFilter);
    }

    setFilteredSurveys(filtered);
  };

  // Separate surveys into CATI and F2F for charge_etude roles
  const catiSurveys = isChargeEtude 
    ? filteredSurveys.filter(survey => survey.poste_qcode !== 'ref')
    : filteredSurveys;
  
  const f2fSurveys = isChargeEtude 
    ? filteredSurveys.filter(survey => survey.poste_qcode === 'ref')
    : [];

  const getStatusIcon = (isActive) => {
    return isActive ? (
      <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />
    ) : (
      <FontAwesomeIcon icon={faCircleXmark} className="text-gray-400" />
    );
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const handleToggleStatus = async (surveyId) => {
    try {
      setTogglingSurveyId(surveyId);
      const response = await api.put(`/cati/surveys/${surveyId}/toggle-status`);
      
      if (response.data.success) {
        // Update the survey in the local state
        setSurveys(prevSurveys => 
          prevSurveys.map(survey => 
            survey.id === surveyId 
              ? { ...survey, is_active: response.data.data.is_active }
              : survey
          )
        );
        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error toggling survey status:', error);
      setError(error.response?.data?.message || 'Erreur lors de la modification du statut');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTogglingSurveyId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10 py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des enquêtes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold text-gray-800">
              {isChargeEtude ? 'ENQUÊTES' : isChefTerrain ? 'ENQUÊTES FACE À FACE' : 'ENQUÊTES CATI'}
            </h1>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {!readOnly && (
              <button
                onClick={() => navigate('/cati/surveys/create')}
                className="px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Nouvelle Enquête
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filtres:</span>
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-shrink-0 w-full sm:w-auto"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="inactive">Inactives</option>
            </select>

            {/* Payment Type Filter */}
            <select
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-shrink-0 w-full sm:w-auto"
            >
              <option value="all">Tous les types</option>
              <option value="per_complete">Au questionnaire</option>
              <option value="per_day">À la journée</option>
            </select>

            {/* Search */}
            <div className="relative flex-1 w-full sm:min-w-0 min-w-0">
              <input
                type="text"
                placeholder={isChargeEtude 
                  ? "Rechercher par titre, ID LimeSurvey ou code enquêteur..."
                  : "Rechercher par titre, ID LimeSurvey ou code poste..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full pr-10 max-w-full"
              />
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
              />
            </div>
          </div>
        </div>

        {/* Summary */}
        {!loading && (
          <div className="mb-6 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                {isChargeEtude ? (
                  <>
                    {catiSurveys.length} enquête{catiSurveys.length !== 1 ? 's' : ''} CATI
                    {f2fSurveys.length > 0 && (
                      <span className="ml-2">
                        | {f2fSurveys.length} enquête{f2fSurveys.length !== 1 ? 's' : ''} Face à Face
                      </span>
                    )}
                    {filteredSurveys.length !== surveys.length && (
                      <span className="text-gray-500">
                        {' '}sur {surveys.length} au total
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {filteredSurveys.length} enquête{filteredSurveys.length !== 1 ? 's' : ''}
                    {filteredSurveys.length !== surveys.length && (
                      <span className="text-gray-500">
                        {' '}sur {surveys.length} au total
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Surveys Tables */}
        {isChargeEtude ? (
          <div className="wrapper">
            {/* Tabs */}
            <ul className="tabs">
              <li 
                className={activeTab === 0 ? 'active' : ''}
                onClick={() => handleTabClick(0)}
              >
                Enquêtes CATI ({catiSurveys.length})
              </li>
              <li 
                className={activeTab === 1 ? 'active' : ''}
                onClick={() => handleTabClick(1)}
              >
                Enquêtes Face à Face ({f2fSurveys.length})
              </li>
            </ul>
            
            {/* Tab Content */}
            <ul className="tab__content">
              {/* CATI Surveys Tab */}
              <li className={activeTab === 0 ? 'active' : ''} style={{ display: activeTab === 0 ? 'block' : 'none' }}>
                <div className="content__wrapper">
                  <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
                    {catiSurveys.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <FontAwesomeIcon icon={faFileText} className="text-4xl text-gray-300 mb-4" />
                        <p className="text-lg">Aucune enquête CATI trouvée</p>
                        <p className="text-sm">
                          {surveys.filter(s => s.poste_qcode !== 'ref').length === 0 
                            ? 'Les enquêtes CATI apparaîtront ici' 
                            : 'Aucun résultat ne correspond à vos critères de recherche'}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Enquête
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ID LimeSurvey
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Code Enquêteur
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Prix par complétion
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Statut
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {catiSurveys.map((survey) => (
                              <tr
                                key={survey.id}
                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => navigate(`/cati/surveys/${survey.id}`)}
                              >
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                      <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                                        <FontAwesomeIcon icon={faFileText} className="text-red-600" />
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        {survey.title || '-'}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {survey.limesurvey_id || '-'}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {survey.poste_qcode || '-'}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {survey.payment_type === 'per_day' ? (
                                    <>
                                      {formatPrice(survey.price_per_day)}
                                      <span className="text-gray-400 text-xs ml-1">/jr</span>
                                    </>
                                  ) : (
                                    formatPrice(survey.price_per_complete)
                                  )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    {getStatusIcon(survey.is_active)}
                                    <span className="ml-2 text-sm text-gray-900">
                                      {getStatusText(survey.is_active)}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                  {!readOnly && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleToggleStatus(survey.id); }}
                                      disabled={togglingSurveyId === survey.id}
                                      className={`inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors min-w-[120px] ${
                                        survey.is_active
                                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      title={survey.is_active ? 'Désactiver l\'enquête' : 'Activer l\'enquête'}
                                    >
                                      {togglingSurveyId === survey.id ? (
                                        <>
                                          <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                          {survey.is_active ? 'Désactivation...' : 'Activation...'}
                                        </>
                                      ) : (
                                        <>
                                          <FontAwesomeIcon
                                            icon={survey.is_active ? faPowerOff : faCheck}
                                            className="mr-2"
                                          />
                                          {survey.is_active ? 'Désactiver' : 'Activer'}
                                        </>
                                      )}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </li>

              {/* Face à Face Surveys Tab */}
              <li className={activeTab === 1 ? 'active' : ''} style={{ display: activeTab === 1 ? 'block' : 'none' }}>
                <div className="content__wrapper">
                  <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
                    {f2fSurveys.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <FontAwesomeIcon icon={faFileText} className="text-4xl text-gray-300 mb-4" />
                        <p className="text-lg">Aucune enquête Face à Face trouvée</p>
                        <p className="text-sm">
                          {surveys.filter(s => s.poste_qcode === 'ref').length === 0 
                            ? 'Les enquêtes Face à Face apparaîtront ici' 
                            : 'Aucun résultat ne correspond à vos critères de recherche'}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Enquête
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ID LimeSurvey
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Code Enquêteur
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Prix par complétion
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Frais Dépl.
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Statut
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {f2fSurveys.map((survey) => (
                              <tr
                                key={survey.id}
                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => navigate(`/cati/surveys/${survey.id}`)}
                              >
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                        <FontAwesomeIcon icon={faFileText} className="text-blue-600" />
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        {survey.title || '-'}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {survey.limesurvey_id || '-'}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {survey.poste_qcode || '-'}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {survey.payment_type === 'per_day' ? (
                                    <>
                                      {formatPrice(survey.price_per_day)}
                                      <span className="text-gray-400 text-xs ml-1">/jr</span>
                                    </>
                                  ) : (
                                    formatPrice(survey.price_per_complete)
                                  )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatPrice(survey.frais_deplacement)}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    {getStatusIcon(survey.is_active)}
                                    <span className="ml-2 text-sm text-gray-900">
                                      {getStatusText(survey.is_active)}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                  {!readOnly && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleToggleStatus(survey.id); }}
                                      disabled={togglingSurveyId === survey.id}
                                      className={`inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors min-w-[120px] ${
                                        survey.is_active
                                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      title={survey.is_active ? 'Désactiver l\'enquête' : 'Activer l\'enquête'}
                                    >
                                      {togglingSurveyId === survey.id ? (
                                        <>
                                          <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                          {survey.is_active ? 'Désactivation...' : 'Activation...'}
                                        </>
                                      ) : (
                                        <>
                                          <FontAwesomeIcon
                                            icon={survey.is_active ? faPowerOff : faCheck}
                                            className="mr-2"
                                          />
                                          {survey.is_active ? 'Désactiver' : 'Activer'}
                                        </>
                                      )}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        ) : (
          /* Original single table for resp_cati */
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {filteredSurveys.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FontAwesomeIcon icon={faFileText} className="text-4xl text-gray-300 mb-4" />
                <p className="text-lg">Aucune enquête trouvée</p>
                <p className="text-sm">
                  {surveys.length === 0 
                    ? 'Les enquêtes apparaîtront ici' 
                    : 'Aucun résultat ne correspond à vos critères de recherche'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enquête
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID LimeSurvey
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code Poste
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prix par complétion
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSurveys.map((survey) => (
                    <tr
                      key={survey.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/cati/surveys/${survey.id}`)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                              <FontAwesomeIcon icon={faFileText} className="text-red-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {survey.title || '-'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {survey.limesurvey_id || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {survey.poste_qcode || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {survey.payment_type === 'per_day' ? (
                          <>
                            {formatPrice(survey.price_per_day)}
                            <span className="text-gray-400 text-xs ml-1">/jr</span>
                          </>
                        ) : (
                          formatPrice(survey.price_per_complete)
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(survey.is_active)}
                          <span className="ml-2 text-sm text-gray-900">
                            {getStatusText(survey.is_active)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        {!readOnly && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(survey.id); }}
                            disabled={togglingSurveyId === survey.id}
                            className={`inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors min-w-[120px] ${
                              survey.is_active
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={survey.is_active ? 'Désactiver l\'enquête' : 'Activer l\'enquête'}
                          >
                            {togglingSurveyId === survey.id ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                {survey.is_active ? 'Désactivation...' : 'Activation...'}
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon
                                  icon={survey.is_active ? faPowerOff : faCheck}
                                  className="mr-2"
                                />
                                {survey.is_active ? 'Désactiver' : 'Activer'}
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Tabs CSS for charge_etude */}
      {isChargeEtude && (
        <style>{`
          .wrapper {
            min-width: 600px;
            max-width: 100%;
            margin: 0 auto;
          }

          .tabs {
            display: table;
            table-layout: fixed;
            width: 100%;
            transform: translateY(5px);
            background-color: #f3f4f6;
            border-radius: 5px 5px 0 0;
          }

          .tabs > li {
            transition-duration: 0.25s;
            display: table-cell;
            list-style: none;
            text-align: center;
            padding: 20px 20px 25px 20px;
            position: relative;
            overflow: hidden;
            cursor: pointer;
            color: #6b7280;
            font-weight: 500;
          }

          .tabs > li:before {
            z-index: -1;
            position: absolute;
            content: "";
            width: 100%;
            height: 120%;
            top: 0;
            left: 0;
            background-color: #e5e7eb;
            transform: translateY(100%);
            transition-duration: 0.25s;
            border-radius: 5px 5px 0 0;
          }

          .tabs > li:hover {
            color: #374151;
            background-color: #e5e7eb;
          }

          .tabs > li:hover:before {
            transform: translateY(70%);
          }

          .tabs > li.active {
            color: #111827;
            background-color: white;
            border-bottom: 2px solid #ef4444;
            font-weight: 600;
          }

          .tabs > li.active:before {
            transform: translateY(0);
            transition-duration: 0.5s;
          }

          .tab__content {
            position: relative;
            width: 100%;
            height: auto;
          }

          .tab__content > li {
            width: 100%;
            position: absolute;
            top: 0;
            left: 0;
            display: none;
            list-style: none;
          }

          .tab__content > li.active {
            display: block;
            position: relative;
          }

          .content__wrapper {
            padding: 20px;
            background-color: white;
            border-radius: 0 0 5px 5px;
          }
        `}</style>
      )}
    </div>
  );
};

export default CatiSurveys;

